import { useEffect, ReactElement, useState } from 'react';

import { AuthError } from 'firebase/auth';

import { useNavigate, useSearchParams } from 'react-router';
import { Link } from 'react-router-dom';

import { useSignInWithEmailAndPassword } from 'react-firebase-hooks/auth';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import LoginIcon from '@mui/icons-material/Login';
import PasswordIcon from '@mui/icons-material/Password';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import { useApp } from '../contextProviders/AppProvider';
import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { useRequestInviteByEmail } from '../dataMutators/useRequestInviteByEmail';
import { validateEmail } from '../utils/validators';

export default function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setPageName } = useApp();
  const { auth } = useAuth();
  const { userState } = useData();
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    password: '',
    isEmailEdited: Boolean(searchParams.get('email')),
    isEmailValid: Boolean(searchParams.get('email')) && validateEmail(searchParams.get('email') || ''),
  });
  const [localError, setLocalError] = useState<AuthError | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [inviteRequestState, setInviteRequestState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const { requestInviteByEmail } = useRequestInviteByEmail();

  const [signInWithEmailAndPassword, loggedInUser, isLoginLoading, loginError] = useSignInWithEmailAndPassword(auth);

  useEffect(() => {
    setPageName('Login');
  }, [setPageName]);

  useEffect(() => {
    if (!isLoginLoading && loggedInUser) {
      navigate('/');
    }
  }, [isLoginLoading, loggedInUser, navigate]);

  useEffect(() => {
    setLocalError(loginError || null);
  }, [loginError]);

  useEffect(() => {
    if (searchParams.get('startingPage') === 'accept-org-invitation') {
      // No need to focus the password input manually as autoFocus will handle it
    }
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocalError(null);
    if (name === 'email') {
      setInviteRequestState('idle');
    }

    const normalizedValue = name === 'email' ? value.toLowerCase() : value;
    
    setFormData((prev) => ({
      ...prev,
      [name]: normalizedValue,
      ...(name === 'email' && {
        isEmailEdited: normalizedValue !== '',
        isEmailValid: normalizedValue !== '' && validateEmail(normalizedValue),
      }),
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await signInWithEmailAndPassword(formData.email, formData.password);
  };

  const handleRequestInvite = async () => {
    setInviteRequestState('sending');
    const isRequested = await requestInviteByEmail(formData.email);
    setInviteRequestState(isRequested ? 'sent' : 'error');
  };

  // A person with a pending invitation has no account yet, so their login fails as a bad email/password
  const isCredentialError = ['auth/invalid-email', 'auth/wrong-password', 'auth/invalid-credential', 'auth/user-not-found']
    .includes(localError?.code ?? '');

  const GenerateErrorMessage = () => {
    if (localError) {
      switch (localError.code) {
        case 'auth/invalid-email':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
          return (
            <Alert variant="filled" severity="error">
              Your email address or password is incorrect. Please try again.
            </Alert>
          );
        case 'auth/insufficient-permission':
        case 'auth/disabled-account':
          return (
            <Alert variant="filled" severity="error">
              Your account is inactive. Please contact us!
            </Alert>
          );
        case 'auth/invalid-claims':
        case 'auth/invalid-action-code':
        case 'auth/internal-error':
          return (
            <Alert variant="filled" severity="error">
              Something went wrong. It&apos;s us, not you. Please try again later.
            </Alert>
          );
        default:
          return (
            <Alert variant="filled" severity="error">
              {localError.message}
            </Alert>
          );
      }
    }
  };

  return (
    <GratiPageWrapper isNoAuthRequired={true}>
      <Stack
        spacing={2}
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          direction: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {searchParams.get('isEmailChange') === 'true' && (
          <Alert severity="success" sx={{ maxWidth: 'md', width: '100%' }}>
            Your email address has been successfully changed. Please log in with your new email address.
          </Alert>
        )}
        {searchParams.get('startingPage') === 'accept-org-invitation' && (
          <Alert severity="info" sx={{ maxWidth: 'md', width: '100%' }}>
            You appear to already have an account. Please enter your password and press Login to continue. If you have
            forgotten your password, please use the &quot;Forgot Password?&quot; link below.
          </Alert>
        )}
        <Typography variant="headlineMedium">Log in to Gratigolf</Typography>
        <Typography variant="bodyMedium" className="secondary-text">
          Enter your email address and password to log in to Gratigolf.
        </Typography>
        <Typography variant="bodyMedium" className="secondary-text">
          If you forgot your password, please tap the &quot;Forgot Password?&quot; link to reset it.
        </Typography>
        <TextField
          fullWidth
          required
          name="email"
          error={formData.isEmailEdited && !formData.isEmailValid}
          label={formData.isEmailEdited && !formData.isEmailValid ? 'Error' : 'Email'}
          helperText={
            formData.isEmailEdited && !formData.isEmailValid ? 'Invalid email address' : 'Valid email address'
          }
          value={formData.email}
          onChange={handleInputChange}
          sx={{ maxWidth: 'md' }}
          slotProps={{
            input: {
              autoComplete: 'username',
            },
          }}
        />
        <TextField
          fullWidth
          required
          name="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          autoComplete="current-password"
          helperText="Enter your password..."
          value={formData.password}
          onChange={handleInputChange}
          autoFocus={searchParams.get('startingPage') === 'accept-org-invitation'}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{ maxWidth: 'md' }}
        />
        <Button type="submit" variant="contained" startIcon={<LoginIcon />}>
          Login
        </Button>
        {localError && <GenerateErrorMessage />}
        {isCredentialError && formData.isEmailValid && inviteRequestState === 'idle' && (
          <Typography variant="bodyMedium" sx={{ textAlign: 'center' }}>
            Did you recently receive an invitation to Gratigolf? If so,{' '}
            <MuiLink component="button" type="button" variant="bodyMedium" onClick={handleRequestInvite}>
              tap here
            </MuiLink>{' '}
            to email me my invitation link.
          </Typography>
        )}
        {inviteRequestState === 'sending' && (
          <Alert severity="info">Sending...</Alert>
        )}
        {inviteRequestState === 'sent' && (
          <Alert severity="success">
            {`If you have an invitation, we've emailed your invitation link to ${formData.email}. Tap the link in that email to create your account.`}
          </Alert>
        )}
        {inviteRequestState === 'error' && (
          <Alert severity="error">
            Unable to send. Please try again, or contact us at admin@gratisports.com.
          </Alert>
        )}
        {isLoginLoading && (
          <Alert variant="filled" severity="info">
            Logging in...
          </Alert>
        )}
        <Box sx={{
          textAlign: "center"
        }}>
          <Button
            component={Link}
            to="/account-forgot-password"
            variant="text"
            color="secondary"
            startIcon={<PasswordIcon />}
          >
            Forgot password?
          </Button>
        </Box>
        {userState.status === 'logged_out' && (
          <Stack spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="bodyMedium" sx={{
              color: "surface.contrastText"
            }}>
              Don&apos;t have an account?{' '}
              <Link to="/accept-org-invitation" style={{ color: 'inherit', textDecoration: 'underline' }}>
                Join
              </Link>{' '}
              today!
            </Typography>
            <Typography variant="bodyMedium" sx={{
              color: "surface.contrastText"
            }}>
              Need help? Contact us at admin@gratisports.com
            </Typography>
          </Stack>
        )}
      </Stack>
    </GratiPageWrapper>
  );
}
